let audioCtx: AudioContext | null = null;
let ringtoneInterval: ReturnType<typeof setInterval> | null = null;
let activeOscillators: OscillatorNode[] = [];
let activeGain: GainNode | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx || audioCtx.state === "closed") {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

function playTone(
  ctx: AudioContext,
  gain: GainNode,
  frequencies: number[],
  durationMs: number
): OscillatorNode[] {
  const oscs = frequencies.map((freq) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    osc.connect(gain);
    osc.start();
    return osc;
  });

  setTimeout(() => {
    oscs.forEach((o) => {
      try { o.stop(); } catch { /* already stopped */ }
    });
  }, durationMs);

  return oscs;
}

/**
 * Plays a classic two-tone phone ringtone: 440Hz + 480Hz,
 * 2 seconds on, 4 seconds off, looping.
 */
export function playRingtone(): void {
  stopRingtone();
  const ctx = getAudioContext();
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.3, ctx.currentTime);
  gain.connect(ctx.destination);
  activeGain = gain;

  const ring = () => {
    activeOscillators = playTone(ctx, gain, [440, 480], 2000);
  };

  ring();
  ringtoneInterval = setInterval(ring, 6000); // 2s on + 4s off
}

/**
 * Plays a ringback / dial tone for the caller: 440Hz + 480Hz,
 * 1 second on, 3 seconds off, looping.
 */
export function playDialTone(): void {
  stopRingtone();
  const ctx = getAudioContext();
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.connect(ctx.destination);
  activeGain = gain;

  const ring = () => {
    activeOscillators = playTone(ctx, gain, [440, 480], 1000);
  };

  ring();
  ringtoneInterval = setInterval(ring, 4000); // 1s on + 3s off
}

/**
 * Stops any currently playing ringtone or dial tone.
 */
export function stopRingtone(): void {
  if (ringtoneInterval) {
    clearInterval(ringtoneInterval);
    ringtoneInterval = null;
  }
  activeOscillators.forEach((o) => {
    try { o.stop(); } catch { /* already stopped */ }
  });
  activeOscillators = [];
  if (activeGain) {
    try { activeGain.disconnect(); } catch { /* ok */ }
    activeGain = null;
  }
}

/**
 * Trigger device vibration if supported.
 * Pattern: vibrate 500ms, pause 500ms, repeat.
 */
export function startVibration(): void {
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    // Vibration pattern: [vibrate, pause, ...] repeated via interval
    navigator.vibrate([500, 500, 500, 500, 500]);
  }
}

export function stopVibration(): void {
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    navigator.vibrate(0);
  }
}
